import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight, CheckCircle, Calendar, Clock, Users, Target, ArrowLeft, Truck, ShoppingCart, Factory, CreditCard, Heart, Building } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Navigation from './Navigation';
import tulioIcon from './assets/tulio.png'  
const IndustryDetailPage = () => {
  const { industryId } = useParams();

  const industryDetails = {
    logistics: {
      name: 'Logistics & Transportation',
      subtitle: 'Optimize fleet operations and supply chain efficiency',
      icon: Truck,
      color: 'from-blue-500 to-cyan-500',
      overview: 'Transform your logistics operations with AI-powered fleet management, predictive maintenance, and real-time optimization systems that reduce costs and improve delivery performance.',
      challenges: [
        'Fleet downtime costing $500-2000 per vehicle per day',
        'Manual route planning reducing efficiency by 20-30%',
        'Lack of real-time visibility across supply chain',
        'Reactive maintenance leading to unexpected breakdowns',
        'Poor customer communication about delivery status'
      ],
      solutions: [
        'IoT-enabled fleet monitoring and predictive maintenance',
        'AI-powered route optimization and dynamic scheduling',
        'Real-time tracking and customer notification systems',
        'Predictive analytics for demand forecasting',
        'Mobile apps for drivers and logistics coordinators',
        'Integration with existing TMS and WMS systems'
      ],
      benefits: [
        { metric: 'Vehicle Downtime', improvement: '40% reduction', description: 'Through predictive maintenance' },
        { metric: 'Fuel Costs', improvement: '25% savings', description: 'Via optimized routing' },
        { metric: 'Customer Satisfaction', improvement: '95% rating', description: 'With real-time updates' },
        { metric: 'Operational Efficiency', improvement: '30% increase', description: 'Automated workflows' }
      ],
      technologies: ['IoT Sensors', 'Machine Learning', 'Real-time Analytics', 'Mobile Development', 'Cloud Infrastructure', 'API Integration'],
      caseStudies: [
        {
          id: 'bigfoot-logistics',
          title: 'Bigfoot Logistics',
          subtitle: 'Fleet Optimization & Predictive Maintenance',
          result: '40% reduction in downtime',
          description: 'Comprehensive IoT fleet management system with predictive maintenance and route optimization.',
          timeline: '16 weeks',
          teamSize: '8 engineers'
        }
      ],
      stats: { projects: '25+', clients: '12', countries: '4' }
    },
    retail: {
      name: 'Retail & E-commerce',
      subtitle: 'Modernize retail operations and enhance customer experience',
      icon: ShoppingCart,
      color: 'from-purple-500 to-pink-500',
      overview: 'Unify your retail operations with integrated POS systems, real-time inventory management, and customer analytics that drive sales growth and operational efficiency.',
      challenges: [
        'Inventory discrepancies leading to 20% stock-outs',
        'Disconnected systems across multiple locations',
        'Poor visibility into sales performance and trends',
        'Manual processes slowing down operations',
        'Inconsistent customer experience across channels'
      ],
      solutions: [
        'Unified ERP system integrating all business functions',
        'Real-time inventory management with automated reordering',
        'Advanced analytics and business intelligence dashboards',
        'Omnichannel customer experience platforms',
        'Mobile POS and staff management applications',
        'Integration with e-commerce and payment systems'
      ],
      benefits: [
        { metric: 'Stock-outs', improvement: '80% reduction', description: 'Automated inventory management' },
        { metric: 'Reporting Speed', improvement: 'Real-time', description: 'Instant business insights' },
        { metric: 'Inventory Turnover', improvement: '50% increase', description: 'Optimized purchasing' },
        { metric: 'Customer Satisfaction', improvement: '92% rating', description: 'Consistent experience' }
      ],
      technologies: ['ERP Systems', 'POS Integration', 'Real-time Analytics', 'CRM', 'Cloud Infrastructure', 'Mobile Development'],
      caseStudies: [
        {
          id: 'cristofori-music',
          title: 'Cristofori Music',
          subtitle: 'Retail & ERP Modernization',
          result: '80% reduction in stock-outs',
          description: 'Integrated ERP system unifying 50+ locations with real-time inventory and analytics.',
          timeline: '20 weeks',
          teamSize: '12 engineers'
        }
      ],
      stats: { projects: '30+', clients: '18', countries: '5' }
    },
    manufacturing: {
      name: 'Industrial & Manufacturing',
      subtitle: 'Enhance production efficiency with smart automation',
      icon: Factory,
      color: 'from-orange-500 to-red-500',
      overview: 'Boost manufacturing productivity with IoT monitoring, predictive maintenance, and quality control systems that minimize downtime and optimize production processes.',
      challenges: [
        'Unplanned equipment downtime costing $50K per hour',
        'Manual quality control processes missing defects',
        'Lack of real-time production visibility',
        'Reactive maintenance increasing costs by 25%',
        'Inefficient resource allocation and scheduling'
      ],
      solutions: [
        'IoT sensor networks for real-time equipment monitoring',
        'Machine learning models for predictive maintenance',
        'Automated quality control and defect detection',
        'Production optimization and scheduling systems',
        'Real-time dashboards for operations management',
        'Integration with existing MES and ERP systems'
      ],
      benefits: [
        { metric: 'Equipment Downtime', improvement: '60% reduction', description: 'Predictive maintenance' },
        { metric: 'Quality Defects', improvement: '75% decrease', description: 'Automated inspection' },
        { metric: 'Production Efficiency', improvement: '35% increase', description: 'Optimized scheduling' },
        { metric: 'Maintenance Costs', improvement: '40% savings', description: 'Preventive approach' }
      ],
      technologies: ['IoT Sensors', 'Machine Learning', 'Computer Vision', 'Edge Computing', 'Real-time Analytics', 'MES Integration'],
      caseStudies: [
        {
          id: 'artc-industrial',
          title: 'ARTC (A*STAR)',
          subtitle: 'Industrial Monitoring System',
          result: '60% reduction in downtime',
          description: 'Comprehensive IoT monitoring system with ML-based anomaly detection for industrial equipment.',
          timeline: '14 weeks',
          teamSize: '6 engineers'
        }
      ],
      stats: { projects: '20+', clients: '15', countries: '3' }
    },
    finance: {
      name: 'Finance & Fintech',
      subtitle: 'Build secure, compliant financial platforms',
      icon: CreditCard,
      color: 'from-green-500 to-teal-500',
      overview: 'Develop secure, scalable financial platforms with AI-powered risk assessment, automated compliance, and exceptional customer experiences that drive digital transformation.',
      challenges: [
        'Complex regulatory compliance requirements',
        'Manual risk assessment processes',
        'Slow customer onboarding taking 5-10 days',
        'Legacy systems limiting innovation',
        'High operational costs for compliance and support'
      ],
      solutions: [
        'Automated compliance monitoring and reporting',
        'AI-powered risk assessment and fraud detection',
        'Digital customer onboarding and KYC automation',
        'Real-time transaction monitoring and alerts',
        'Secure API development for third-party integrations',
        'Modern cloud infrastructure with enhanced security'
      ],
      benefits: [
        { metric: 'Onboarding Time', improvement: '80% faster', description: 'Digital automation' },
        { metric: 'Compliance Costs', improvement: '50% reduction', description: 'Automated processes' },
        { metric: 'Fraud Detection', improvement: '95% accuracy', description: 'AI-powered models' },
        { metric: 'Customer Satisfaction', improvement: '90% rating', description: 'Seamless experience' }
      ],
      technologies: ['AI/ML', 'Blockchain', 'API Development', 'Cloud Security', 'Real-time Processing', 'Compliance Automation'],
      caseStudies: [
        {
          id: 'fintech-ai-platform',
          title: 'FinTech AI Assistant',
          subtitle: 'Intelligent Customer Service Automation',
          result: '80% reduction in response time',
          description: 'AI-powered customer service platform with automated query resolution and risk assessment.',
          timeline: '12 weeks',
          teamSize: '5 engineers'
        }
      ],
      stats: { projects: '15+', clients: '10', countries: '4' }
    },
    healthcare: {
      name: 'Healthcare & Life Sciences',
      subtitle: 'Digitize healthcare with compliant, secure systems',
      icon: Heart,
      color: 'from-red-500 to-pink-500',
      overview: 'Transform healthcare delivery with HIPAA-compliant systems, patient data analytics, telemedicine platforms, and AI-powered diagnostic tools that improve patient outcomes.',
      challenges: [
        'Fragmented patient data across multiple systems',
        'Complex HIPAA compliance requirements',
        'Manual processes reducing care efficiency',
        'Limited telemedicine capabilities',
        'Difficulty in generating actionable health insights'
      ],
      solutions: [
        'Unified patient data platforms with HIPAA compliance',
        'AI-powered diagnostic and predictive analytics',
        'Telemedicine and remote patient monitoring systems',
        'Electronic health record (EHR) integration',
        'Real-time health monitoring and alert systems',
        'Secure patient portals and communication platforms'
      ],
      benefits: [
        { metric: 'Data Integration', improvement: '100% unified', description: 'Single patient view' },
        { metric: 'Diagnostic Accuracy', improvement: '30% increase', description: 'AI assistance' },
        { metric: 'Patient Satisfaction', improvement: '88% rating', description: 'Digital convenience' },
        { metric: 'Operational Efficiency', improvement: '40% improvement', description: 'Automated workflows' }
      ],
      technologies: ['HIPAA Compliance', 'AI/ML', 'Telemedicine', 'EHR Integration', 'Real-time Monitoring', 'Secure Cloud'],
      caseStudies: [
        {
          id: 'healthcare-data-platform',
          title: 'Healthcare Analytics Platform',
          subtitle: 'Patient Data Integration & Analytics',
          result: '30% improvement in diagnosis',
          description: 'Secure healthcare data platform with real-time analytics and predictive modeling.',
          timeline: '18 weeks',
          teamSize: '10 engineers'
        }
      ],
      stats: { projects: '12+', clients: '8', countries: '2' }
    },
    government: {
      name: 'Government & Public Sector',
      subtitle: 'Modernize public services with digital transformation',
      icon: Building,
      color: 'from-indigo-500 to-blue-500',
      overview: 'Enable digital government transformation with citizen-centric portals, data integration platforms, and modern infrastructure that improves public service delivery.',
      challenges: [
        'Legacy systems hindering digital transformation',
        'Siloed data across government departments',
        'Poor citizen experience with government services',
        'Manual processes causing delays and inefficiency',
        'Security and compliance requirements'
      ],
      solutions: [
        'Digital citizen service portals and applications',
        'Inter-agency data integration and sharing platforms',
        'Modern infrastructure and cloud migration',
        'Automated workflow and approval systems',
        'Business intelligence and analytics dashboards',
        'Cybersecurity and compliance frameworks'
      ],
      benefits: [
        { metric: 'Service Delivery', improvement: '50% faster', description: 'Digital processes' },
        { metric: 'Citizen Satisfaction', improvement: '85% rating', description: 'Online services' },
        { metric: 'Operational Costs', improvement: '30% reduction', description: 'Automation' },
        { metric: 'Data Integration', improvement: '90% coverage', description: 'Unified systems' }
      ],
      technologies: ['Digital Portals', 'System Integration', 'Cloud Migration', 'Data Analytics', 'Cybersecurity', 'Workflow Automation'],
      caseStudies: [
        {
          id: 'govtech-platform',
          title: 'GovTech Digital Platform',
          subtitle: 'Citizen Services Modernization',
          result: '50% faster service delivery',
          description: 'Comprehensive digital transformation of citizen-facing government services.',
          timeline: '24 weeks',
          teamSize: '15 engineers'
        }
      ],
      stats: { projects: '18+', clients: '6', countries: '3' }
    }
  };

  const industry = industryDetails[industryId];
  if (!industry) {
    return <div>Industry not found</div>;
  }

  const Icon = industry.icon;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
             <Link to="/" className="flex items-center space-x-3">
            <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <Navigation />
          </div>
        </div>
      </header>

      {/* Industry Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <Link to="/industries" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Industries
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className={`w-16 h-16 bg-gradient-to-br ${industry.color} rounded-2xl flex items-center justify-center mb-6`}>
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">{industry.name}</h1>
              <p className="text-xl text-slate-600 mb-8">{industry.subtitle}</p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Consultation
                </Button>
                <Link to="/case-studies">
                  <Button size="lg" variant="outline">
                    View Case Studies
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              
              <div className="grid grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-slate-900">{industry.stats.projects}</div>
                  <div className="text-sm text-slate-600">Projects</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{industry.stats.clients}</div>
                  <div className="text-sm text-slate-600">Clients</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{industry.stats.countries}</div>
                  <div className="text-sm text-slate-600">Countries</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold mb-6">Industry Impact</h3>
              <div className="space-y-4">
                {industry.benefits.slice(0, 3).map((benefit, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <div>
                      <div className="font-semibold text-slate-900">{benefit.metric}</div>
                      <div className="text-sm text-slate-600">{benefit.description}</div>
                    </div>
                    <div className="text-lg font-bold text-emerald-600">{benefit.improvement}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">Industry Overview</h2>
          <p className="text-lg text-slate-600 leading-relaxed">{industry.overview}</p>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Challenges We Solve</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold mb-6 text-red-600">Common Industry Challenges</h3>
              <div className="space-y-4">
                {industry.challenges.map((challenge, index) => (
                  <div key={index} className="flex items-start p-4 bg-white rounded-lg shadow-sm">
                    <Target className="w-5 h-5 text-red-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">{challenge}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold mb-6 text-emerald-600">Our Solutions</h3>
              <div className="space-y-4">
                {industry.solutions.map((solution, index) => (
                  <div key={index} className="flex items-start p-4 bg-white rounded-lg shadow-sm">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-slate-700">{solution}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Technologies We Use</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {industry.technologies.map((tech, index) => (
              <div key={index} className="bg-slate-100 rounded-lg p-4 text-center">
                <span className="text-slate-700 font-medium">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Success Stories in {industry.name}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {industry.caseStudies.map((study, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <Badge className="bg-emerald-100 text-emerald-700">Case Study</Badge>
                    <div className="text-right text-sm text-slate-600">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {study.timeline}
                      </div>
                      <div className="flex items-center mt-1">
                        <Users className="w-4 h-4 mr-1" />
                        {study.teamSize}
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{study.title}</h3>
                  <h4 className="text-lg font-semibold text-emerald-700 mb-4">{study.subtitle}</h4>
                  <p className="text-slate-600 mb-6">{study.description}</p>
                  
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-600">{study.result}</div>
                      <div className="text-sm text-slate-600">Key Result Achieved</div>
                    </div>
                  </div>
                  
                  <Link to={`/case-studies/${study.id}`}>
                    <Button className="w-full bg-slate-900 hover:bg-slate-800">
                      Read Full Case Study
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/case-studies">
              <Button size="lg" variant="outline">
                View All Case Studies
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your {industry.name} Operations?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Let's discuss how our proven solutions can address your specific challenges and drive measurable results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Industry Consultation
            </Button>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-slate-400 text-white hover:bg-white/10">
                View Our Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustryDetailPage;